import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "react-bootstrap";
import { FaKey, FaSearch, FaUsers, FaChartLine } from "react-icons/fa";
import { MdPerson, MdEmail, MdBusiness, MdAccessTime } from "react-icons/md";
import { getLicenseUsage } from "../../redux/actions/settingActions";
import Pagination from "../../../helper/Pagination";

const LicenseUsageModal = ({ isOpen, onClose, license }) => {
  const dispatch = useDispatch();
  const { licenseUsage = [], usagePagination = {}, usageLoading = false, usageStats = {} } = useSelector(state => state.setting || {});
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (isOpen && license?._id) {
      fetchUsageData(1, "");
    }
  }, [isOpen, license]);

  const fetchUsageData = (page, search) => {
    if (license?.itsalesPartner?.id) {
      dispatch(getLicenseUsage({
        licenseId: license._id,
        itsalesId: license.itsalesPartner.id,
        page,
        search,
        limit: 10
      }));
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
    fetchUsageData(1, value);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchUsageData(page, searchTerm);
  };

  const handleClose = () => {
    setCurrentPage(1);
    setSearchTerm("");
    onClose();
  };

  return (
    <Modal
      show={isOpen}
      onHide={handleClose}
      size="xl"
      centered
      className="license-modal"
    >
      <Modal.Header closeButton className="modal-header-custom">
        <Modal.Title className="d-flex align-items-center justify-content-center">
          <div className="modal-icon">
            <FaKey />
          </div>
          <div className="align-items-center">
            <h5 className="mb-0">{license?.name}</h5>
            <p className="fs-6 mt-0 mb-0">License Usage Analytics</p>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-0 modal-body-custom">
        {/* Stats Overview */}
        {usageStats && Object.keys(usageStats).length > 0 && (
          <div className="modal-stats-section">
            <div className="row g-3">
              <div className="col-md-4">
                <div className="stat-card usage-card">
                  <div className="stat-icon">
                    <FaChartLine />
                  </div>
                  <div className="stat-content">
                    <div className="stat-value">{usageStats.totalUsage || 0}</div>
                    <div className="stat-label">Total Usage</div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="stat-card users-card">
                  <div className="stat-icon">
                    <FaUsers />
                  </div>
                  <div className="stat-content">
                    <div className="stat-value">{usageStats.uniqueUsage || 0}</div>
                    <div className="stat-label">Unique Users</div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="stat-card status-card">
                  <div className="stat-icon">
                    <MdAccessTime />
                  </div>
                  <div className="stat-content">
                    <div className="stat-value">{usageStats.averageUsage || 0}</div>
                    <div className="stat-label">Average Usage</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users List */}
        <div className="users-section">
          <div className="users-header">
            <h6 className="section-title mb-0">
              <FaUsers className="me-2" />
              License Users
              <span className="badge bg-primary ms-2">
                {licenseUsage?.length || 0}
              </span>
            </h6>
            <div className="position-relative d-flex justify-content-end mb-1">
              <input
                type="search"
                placeholder="Search users..."
                className="form-input"
                value={searchTerm}
                onChange={handleSearchChange}
                style={{ width: "200px" }}
              />
              {searchTerm === "" && (
                <div
                  className="position-absolute"
                  style={{
                    top: "50%",
                    right: "13px",
                    transform: "translateY(-50%)",
                    color: "#666",
                  }}
                >
                  <FaSearch />
                </div>
              )}
            </div>
          </div>

          <div className="users-content scroll-container">
            {usageLoading ? (
              <div className="loading-state">
                <div className="spinner-border text-primary" role="status"></div>
                <p className="mt-3 mb-0">Loading usage data...</p>
              </div>
            ) : licenseUsage.length === 0 ? (
              <div className="text-center py-5">
                <p className="text-muted">No usage records found</p>
              </div>
            ) : (
              <div className="table-container">
                <table className="partners-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>User Details</th>
                      <th>Email</th>
                      <th>Company</th>
                      <th>Usage Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {licenseUsage.map((usage, index) => (
                      <tr key={usage._id || index}>
                        <td>
                          <div className="user-number">{(currentPage - 1) * 10 + index + 1}</div>
                        </td>
                        <td>
                          <div className="user-info">
                            <div className="user-avatar">
                              <MdPerson />
                            </div>
                            <div>
                              <div className="user-name">
                                {usage.firstName} {usage.lastName}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="user-email">
                            <MdEmail className="me-1" />
                            {usage.userEmail}
                          </div>
                        </td>
                        <td>
                          <div className="company-info">
                            <div className="company-name">
                              <MdBusiness className="me-1" />
                              {usage.oemCompany}
                            </div>
                            <div className="partner-name">
                              via {usage.partnerCompany}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="usage-date">
                            {new Date(usage.createdAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {usagePagination.totalPages > 1 && (
              <div className="pagination-container">
                <Pagination
                  currentPage={currentPage}
                  totalPages={usagePagination.totalPages}
                  totalItems={usagePagination.totalItems}
                  itemsPerPage={usagePagination.itemsPerPage || 10}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default LicenseUsageModal;